import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import urlParse from 'url-parse';
import qs from 'querystringify';
import {
  Box,
  Card,
  Page,
  ResourceList,
  Text,
  Pagination,
} from '../components';
import { Month } from '../utils/constants';
import { getUserDetails, getSelectedLanguage, getIsRedesign } from '../modules/selectors';
import api from '../utils/services';

const propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
    params: PropTypes.shape({
      carrierId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    agentNPN: PropTypes.string,
    token: PropTypes.string,
    userID: PropTypes.string,
    userRole: PropTypes.string,
  }),
  selectedLanguage: PropTypes.shape({
    languageId: PropTypes.number,
  }).isRequired,
  isRedesign: PropTypes.bool.isRequired,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
};
const defaultProps = {
  user: null,
};

class Plans extends Component {
  constructor(props) {
    super(props);
    const { location: { search } } = props;
    const { query } = urlParse(search);
    let { page } = qs.parse(query);
    if (!page) {
      page = 1;
    }

    this.state = {
      loading: true,
      plans: null,
      currentPage: page,
      plansPerPage: 6,
      totalPlanCount: 0,
      selectedItems: [],
      resourceListLoading: true,
      searchTerm: '',
      appliedFilters: [
        {
          key: 'planYearFilter',
          value: this.calculateDefaultPlanYear(),
          label: 'Plan Year',
        },
      ],
    };
  }


  componentDidMount() {
    const { appliedFilters } = this.state;
    this.handleApplyFilters(appliedFilters);
  }

  componentDidUpdate(prevProps, prevState) {
    const { searchTerm, appliedFilters } = this.state;
    if (prevState.searchTerm !== searchTerm) {
      this.handleApplyFilters(appliedFilters);
    }
  }

  calculateDefaultPlanYear = () => {
    const currentDate = new Date();
    let currentYear;
    if (currentDate.getMonth() >= Month.August) {
      currentYear = currentDate.getFullYear() + 1;
    } else {
      currentYear = currentDate.getFullYear();
    }
    return currentYear;
  }

  handlePageChange = (current) => {
    const { plansPerPage, appliedFilters, searchTerm } = this.state;
    const offset = (current - 1) * plansPerPage;
    const { match: { params: { carrierId } } } = this.props;
    const planYear = [...new Set(appliedFilters.map(filter => parseInt(filter.value, 10)))];
    api.getPlans({
      limit: plansPerPage,
      offset,
      carrierId,
      searchTerm,
      planYear,
    }).then((response) => {
      this.setState({
        plans: response,
        currentPage: current,
      });
    })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  handleSelectionChange = (selectedItems) => {
    this.setState({ selectedItems });
  }

  handleOnAction = () => {
    const {
      history, match, selectedLanguage: { languageId }, isRedesign,
    } = this.props;
    const { selectedItems } = this.state;
    const pathname = `${match.url}/compare?ids=${selectedItems.join(',')}&languageId=${languageId}${isRedesign ? '&isRedesign=true' : ''}`;
    history.push(pathname);
  }

  handleFiltersChange = (appliedFilters) => {
    this.setState({ appliedFilters, resourceListLoading: true });
    this.handleApplyFilters(appliedFilters);
  }

  handleQueryValueChange = (event) => {
    this.setState({ searchTerm: event.target.value });
  }

  handleApplyFilters = (appliedFilters) => {
    const { plansPerPage, searchTerm, currentPage } = this.state;
    const offset = (currentPage - 1) * plansPerPage;

    const planYearFilters = appliedFilters.filter(filter => filter.key === 'planYearFilter');
    const { match: { params: { carrierId } } } = this.props;
    let planYear = [];
    if (planYearFilters.length > 0) {
      planYear = [...new Set(planYearFilters.map(filter => parseInt(filter.value, 10)))];
    }

    const requests = [api.getPlansCount(carrierId, searchTerm, planYear)];
    requests.push(api.getPlans({
      limit: plansPerPage,
      offset,
      carrierId,
      searchTerm,
      planYear,
    }));

    Promise.all(requests)
      .then(([CountResponse, planResponse]) => {
        this.setState({
          plans: planResponse,
          totalPlanCount: CountResponse,
          currentPage,
        });
      })
      .finally(() => {
        this.setState({ loading: false, resourceListLoading: false });
      });
  }

  render() {
    const {
      match, user, selectedLanguage: { languageId }, isRedesign,
    } = this.props;
    const {
      loading,
      plans,
      currentPage,
      plansPerPage,
      totalPlanCount,
      selectedItems,
      appliedFilters,
      resourceListLoading,
      searchTerm,
    } = this.state;

    const planCountMarkup = plans && `Total plans: ${totalPlanCount}`;

    const currentYear = new Date().getFullYear();
    const planYears = [currentYear - 1, currentYear, currentYear + 1];

    const paginationMarkup = (totalPlanCount > plansPerPage) ? (
      <Box marginTop="1em">
        <Pagination
          currentPage={currentPage}
          onPageChange={this.handlePageChange}
          pageSize={plansPerPage}
          totalCount={totalPlanCount}
        />
      </Box>
    ) : null;

    return (
      <Page
        title="Plans"
        subtitle={user ? planCountMarkup : '(This is the "Quotes Page" in AQE)'}
        primaryAction={user ? { content: 'Add plan', href: `${match.url}/new` } : undefined}
      >
        <Card loading={loading}>
          <ResourceList
            resourceName={{ singular: 'plan', plural: 'plans' }}
            items={plans || []}
            promotedBulkActions={[{ content: 'Compare Plans', onAction: this.handleOnAction }]}
            selectedItems={selectedItems}
            onSelectionChange={this.handleSelectionChange}
            loading={resourceListLoading}
            renderItem={plan => (
              <ResourceList.Item key={plan.id} url={`${match.url}/${plan.id}?languageId=${languageId}${isRedesign ? '&isRedesign=true' : ''}&page=${currentPage}`} id={plan.id}>
                <Text as="h3">
                  {plan.name}
                </Text>
                <div>
                  <Text>
                    {`${plan.cmsContractId}-${plan.cmsPbpId}, ${plan.cmsYear}`}
                  </Text>
                </div>
              </ResourceList.Item>
            )}
            filterControl={(
              <ResourceList.FilterControl
                filters={[
                  {
                    key: 'planYearFilter',
                    label: 'Plan Year',
                    operatorText: 'is',
                    type: 'Select',
                    options: planYears,
                  },
                ]}
                queryValue={searchTerm}
                onQueryChange={this.handleQueryValueChange}
                appliedFilters={appliedFilters}
                onFilterChange={this.handleFiltersChange}
              />
        )}
          />
        </Card>
        {paginationMarkup}
      </Page>
    );
  }
}
function mapStateToProps(state) {
  return {
    user: getUserDetails(state),
    selectedLanguage: getSelectedLanguage(state),
    isRedesign: getIsRedesign(state),
  };
}
Plans.propTypes = propTypes;
Plans.defaultProps = defaultProps;
export default connect(mapStateToProps)(Plans);
